import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { StrapiConfig } from '../../utils/Setting';

const ProjectCard = ({ 
  project, 
  orderedImages, 
  onEdit, 
  onDelete,
  showFullDescription = false,
  readOnly = false
}) => {
  // Helper function to get image URL (handles both absolute and relative URLs)
  const getImageUrl = (image) => {
    if (!image || !image.url) return '';
    
    // If URL is already absolute (starts with http:// or https://), use it directly
    if (image.url.startsWith('http://') || image.url.startsWith('https://')) {
      return image.url;
    }
    
    // Otherwise, prepend StrapiConfig.url for relative URLs
    return `${StrapiConfig.url}${image.url}`;
  };

  const handleCardClick = (e) => {
    if (!e.defaultPrevented) {
      onEdit(project);
    }
  };

  return (
    <Box 
      onClick={handleCardClick}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        padding: '16px 8px',
        // The card used to be a rigid 320x320 box, which overflowed its grid
        // track once the page had any padding on a phone.
        width: '100%',
        maxWidth: { xs: '100%', sm: '320px' },
        height: { xs: 'auto', sm: '320px' },
        boxSizing: 'border-box',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Images - Show up to 3 images based on order */}
      {orderedImages && orderedImages.length > 0 && (
        <Box sx={{ height: '200px', overflow: 'hidden', borderRadius: '16px' }}>
          {orderedImages.length === 1 && (
            <Box
              component="img"
              src={getImageUrl(orderedImages[0])}
              alt={project.project_name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          )}
          
          {orderedImages.length === 2 && (
            <Box sx={{ display: 'flex', height: '100%', gap: '4px' }}>
              <Box
                component="img"
                src={getImageUrl(orderedImages[0])}
                alt={`${project.project_name} - Image 1`}
                sx={{
                  width: '50%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <Box
                component="img"
                src={getImageUrl(orderedImages[1])}
                alt={`${project.project_name} - Image 2`}
                sx={{
                  width: '50%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}
          
          {orderedImages.length >= 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '6px' }}>
              {/* Top row - Images 2 and 3: 50% height, 50% width each */}
              <Box sx={{ display: 'flex', height: '50%', gap: '6px' }}>
                <Box
                  component="img"
                  src={getImageUrl(orderedImages[1])}
                  alt={`${project.project_name} - Image 2`}
                  sx={{
                    width: '50%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <Box
                  component="img"
                  src={getImageUrl(orderedImages[2])}
                  alt={`${project.project_name} - Image 3`}
                  sx={{
                    width: '50%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
              {/* Bottom image - Image 1: 50% height, 100% width */}
              <Box
                component="img"
                src={getImageUrl(orderedImages[0])}
                alt={`${project.project_name} - Image 1`}
                sx={{
                  width: '100%',
                  height: '50%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}
        </Box>
      )}
      
      <Box sx={{ p: 1.5 }}>
        {/* Title and Action buttons row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
            {project.project_name}
          </Typography>
          
          {/* Action buttons - Only show if not readOnly */}
          {!readOnly && (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {onEdit && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(project);
                  }}
                  sx={{
                    backgroundColor: '#F3F4F6',
                    width: 32,
                    height: 32,
                    border: '1px solid #E5E7EB',
                    borderRadius: '5px',
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: '#E5E7EB',
                    }
                  }}
                >
                  <img 
                    src="/assets/images/edit_icon.png" 
                    alt="Edit" 
                    style={{ width: '24px', height: '24px' }}
                  />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(project);
                  }}
                  sx={{
                    backgroundColor: '#F3F4F6',
                    width: 32,
                    height: 32,
                    border: '1px solid #E5E7EB',
                    borderRadius: '5px',
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: '#FEE2E2',
                    }
                  }}
                >
                  <img 
                    src="/assets/images/trash_icon.png" 
                    alt="Delete" 
                    style={{ width: '24px', height: '24px' }}
                  />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
        
        {/* Location - Full width */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#999', 
            mb: 1,
            width: '100%',
            wordBreak: 'break-word'
          }}
        >
          {project.location || 'No location specified'}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            {project.unit_type || 'Unit type'}
          </Typography>
          {(project.date || project.project_date) && (
            <Typography variant="caption" sx={{ color: '#999' }}>
              {new Date(project.date || project.project_date).toLocaleDateString('en-GB', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectCard;
